import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Crown,
  Film,
  GripVertical,
  Info,
  ListOrdered,
  Loader2,
  Medal,
  RefreshCw,
  Save,
  Send,
  Trash2,
  Trophy,
  UploadCloud,
  X
} from 'lucide-react';
import { KWC2026_TRICKS } from '../constants';
import {
  KWC2026_POINT_MAX_TRICK_COUNT,
  KWC2026_POINT_MAX_VIDEO_DURATION_SECONDS,
  KWC2026_POINT_MAX_VIDEO_DURATION_TOLERANCE_SECONDS,
  KWC2026_POINT_MAX_VIDEO_SIZE,
  computeKwc2026PointScore,
  fetchKwc2026PointEntry,
  fetchKwc2026PointLeaderboard,
  fetchKwc2026PointProfile,
  formatKwc2026PointDuration,
  formatKwc2026PointTimestamp,
  getKwc2026PointTrickName,
  isKwc2026PointLocalMockUserId,
  isKwc2026PointVideoFailed,
  isKwc2026PointVideoFile,
  isKwc2026PointVideoProcessing,
  isKwc2026PointVideoReady,
  readKwc2026PointVideoDuration,
  saveKwc2026PointTricks,
  submitKwc2026PointEntry,
  uploadKwc2026PointVideo
} from '../kwc2026Point';
import type {
  Kwc2026PointEntryDetail,
  Kwc2026PointLeaderboardEntry,
  Kwc2026PointProfile,
  TournamentTrick,
  User
} from '../types';

interface KWC2026PointModeProps {
  user: User;
  onBack: () => void;
}

type SubmissionPhase = 'idle' | 'confirm' | 'submitting';

const PANEL_CLASS = 'min-w-0 rounded-[24px] border border-amber-400/20 bg-[linear-gradient(150deg,rgba(34,22,8,0.72),rgba(10,10,16,0.88))] p-4 shadow-[0_0_28px_rgba(245,158,11,0.08)] md:p-5';
const SECTION_TITLE_CLASS = 'font-orbitron text-sm uppercase tracking-[0.18em] text-amber-200';
const PRIMARY_BUTTON_CLASS = 'inline-flex items-center justify-center gap-2 rounded-xl border border-amber-400/40 bg-amber-500/20 px-4 py-2.5 text-sm font-semibold text-amber-100 transition-colors hover:bg-amber-500/30 disabled:cursor-not-allowed disabled:opacity-40';
const GHOST_BUTTON_CLASS = 'inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40';

// 等级分组的招式总表：15 个等级 × 10 个招式
const kwc2026PointTrickGroups = (() => {
  const groups = new Map<number, TournamentTrick[]>();
  KWC2026_TRICKS.forEach((trick) => {
    if (!groups.has(trick.score)) groups.set(trick.score, []);
    groups.get(trick.score)!.push(trick);
  });
  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([level, tricks]) => ({ level, tricks }));
})();

const kwc2026PointTrickList = kwc2026PointTrickGroups.flatMap((group) => group.tricks);

const splitKwcPointTrickName = (trickId: string) => {
  const rawName = getKwc2026PointTrickName(trickId);
  const [firstLine = '', secondLine = '', thirdLine = ''] = rawName.split('\n');
  const title = firstLine.replace(/^\d+-\d+\s*/, '').trim() || firstLine.trim();
  return {
    title,
    japanese: secondLine.trim(),
    chinese: thirdLine.trim() || secondLine.trim()
  };
};

const getAvatarInitial = (name: string) => (String(name || '?').trim().charAt(0) || '?').toUpperCase();

const RANK_STYLES: Record<number, string> = {
  1: 'border-amber-300/50 bg-amber-400/15 text-amber-100',
  2: 'border-zinc-300/40 bg-white/10 text-zinc-100',
  3: 'border-orange-400/40 bg-orange-500/15 text-orange-100'
};

// 前三名单独一排重点展示的样式（金 / 银 / 铜）
const PODIUM_STYLES: Record<number, { card: string; badge: string; score: string }> = {
  1: {
    card: 'border-amber-300/60 bg-[linear-gradient(165deg,rgba(251,191,36,0.24),rgba(10,10,16,0.75))] shadow-[0_10px_30px_rgba(245,158,11,0.18)]',
    badge: 'border-amber-300/70 bg-amber-400/20 text-amber-100',
    score: 'text-amber-200'
  },
  2: {
    card: 'border-zinc-300/45 bg-[linear-gradient(165deg,rgba(212,212,216,0.2),rgba(10,10,16,0.75))]',
    badge: 'border-zinc-300/60 bg-white/10 text-zinc-100',
    score: 'text-zinc-100'
  },
  3: {
    card: 'border-orange-400/45 bg-[linear-gradient(165deg,rgba(251,146,60,0.2),rgba(10,10,16,0.75))]',
    badge: 'border-orange-300/60 bg-orange-500/20 text-orange-100',
    score: 'text-orange-100'
  }
};

const formatVideoSize = (size?: number) => {
  if (!Number.isFinite(Number(size)) || Number(size) <= 0) return '';
  const megabytes = Number(size) / (1024 * 1024);
  return megabytes >= 1 ? `${megabytes.toFixed(1)} MB` : `${Math.max(1, Math.round(Number(size) / 1024))} KB`;
};

export function KWC2026PointMode({ user, onBack }: KWC2026PointModeProps) {
  const [profile, setProfile] = useState<Kwc2026PointProfile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');

  const [selectedTricks, setSelectedTricks] = useState<string[]>([]);
  const [isSavingTricks, setIsSavingTricks] = useState(false);
  const [trickNotice, setTrickNotice] = useState('');
  const [trickError, setTrickError] = useState('');
  const [expandedLevels, setExpandedLevels] = useState<Record<number, boolean>>({});
  const [activeLevel, setActiveLevel] = useState<number | 'ALL'>('ALL');

  const [videoError, setVideoError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const [submissionPhase, setSubmissionPhase] = useState<SubmissionPhase>('idle');
  const [submissionError, setSubmissionError] = useState('');
  const [submissionNotice, setSubmissionNotice] = useState('');

  const [leaderboard, setLeaderboard] = useState<Kwc2026PointLeaderboardEntry[]>([]);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = useState(true);
  const [leaderboardError, setLeaderboardError] = useState('');
  const [detailEntry, setDetailEntry] = useState<Kwc2026PointEntryDetail | null>(null);
  const [detailLoadingUserId, setDetailLoadingUserId] = useState<string | null>(null);
  const [detailError, setDetailError] = useState('');

  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isTrickWindowOpen, setIsTrickWindowOpen] = useState(false);
  const [isVideoWindowOpen, setIsVideoWindowOpen] = useState(false);
  // 选招窗口里「选择招式 / 招式顺序」是同一区域点击切换。
  const [trickWindowTab, setTrickWindowTab] = useState<'picker' | 'order'>('picker');

  const videoInputRef = useRef<HTMLInputElement | null>(null);
  const leaderboardRef = useRef<HTMLDivElement | null>(null);
  const selectedRowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const dragStateRef = useRef<{ pointerId: number; fromIndex: number } | null>(null);

  const scoreSummary = useMemo(() => computeKwc2026PointScore(selectedTricks), [selectedTricks]);

  const savedTricks = profile?.selectedTricks ?? [];
  const hasUnsavedTricks = useMemo(() => {
    if (selectedTricks.length !== savedTricks.length) return true;
    return selectedTricks.some((trickId, index) => trickId !== savedTricks[index]);
  }, [savedTricks, selectedTricks]);

  const submission = profile?.submission ?? null;
  const savedVideo = profile?.video ?? null;
  const isVideoProcessing = isKwc2026PointVideoProcessing(savedVideo);
  const isVideoReady = isKwc2026PointVideoReady(savedVideo);
  const didVideoFail = isKwc2026PointVideoFailed(savedVideo);
  // 保存招式之后再录的视频才算数：否则先用低等级招式组录、再把招式组改成高等级，
  // 就会变成「分数按新招式算、视频画面是旧招式」。
  const isVideoStale = useMemo(() => {
    const savedTricksAt = Number(profile?.savedTricksAt);
    const videoUploadedAt = Number(savedVideo?.uploadedAt);
    if (!Number.isFinite(savedTricksAt) || savedTricksAt <= 0) return false;
    if (!Number.isFinite(videoUploadedAt) || videoUploadedAt <= 0) return false;
    return videoUploadedAt < savedTricksAt;
  }, [profile?.savedTricksAt, savedVideo?.uploadedAt]);
  // 视频要等独立转码机处理完（status=ready）才能投稿，保证排行榜里都是可播放的视频。
  const canSubmit = selectedTricks.length > 0 && isVideoReady && !hasUnsavedTricks && !isVideoStale;
  const videoStatusLabel = isVideoStale
    ? '需重新上传'
    : isVideoReady
      ? '已就绪'
      : isVideoProcessing
        ? `转码中${savedVideo?.processingProgress !== undefined ? ` ${savedVideo.processingProgress}%` : ''}`
        : didVideoFail
          ? '转码失败'
          : savedVideo
            ? '待处理'
            : '未上传';

  const loadProfile = useCallback(async () => {
    setIsProfileLoading(true);
    setProfileError('');
    try {
      const nextProfile = await fetchKwc2026PointProfile();
      setProfile(nextProfile);
      setSelectedTricks(nextProfile.selectedTricks);
    } catch (error) {
      setProfileError((error as Error)?.message || '活动数据加载失败');
    } finally {
      setIsProfileLoading(false);
    }
  }, []);

  const loadLeaderboard = useCallback(async () => {
    setIsLeaderboardLoading(true);
    setLeaderboardError('');
    try {
      const entries = await fetchKwc2026PointLeaderboard();
      setLeaderboard(entries);
    } catch (error) {
      setLeaderboardError((error as Error)?.message || '排行榜加载失败');
    } finally {
      setIsLeaderboardLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfile();
    void loadLeaderboard();
  }, [loadProfile, loadLeaderboard]);

  // 视频交给独立转码机后要先等它处理完：转码期间轮询 profile，好了自动切成可播放。
  useEffect(() => {
    if (!isVideoProcessing) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      void (async () => {
        try {
          const nextProfile = await fetchKwc2026PointProfile();
          if (!cancelled) setProfile(nextProfile);
        } catch {
          // 轮询失败就等下一轮，不打断用户操作
        }
      })();
    }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isVideoProcessing]);

  useEffect(() => {
    if (!detailEntry && !isEditorOpen && !isTrickWindowOpen && !isVideoWindowOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [detailEntry, isEditorOpen, isTrickWindowOpen, isVideoWindowOpen]);

  // 打开投稿窗口时把上一次的提示清掉，避免旧的成功/失败提示串场。
  const openEditor = () => {
    setTrickNotice('');
    setTrickError('');
    setVideoError('');
    setSubmissionError('');
    setSubmissionNotice('');
    setIsTrickWindowOpen(false);
    setIsVideoWindowOpen(false);
    setIsEditorOpen(true);
  };

  // 关投稿窗口时把两个子弹窗一起收掉，避免下次打开时残留。
  const closeEditor = () => {
    setIsTrickWindowOpen(false);
    setIsVideoWindowOpen(false);
    setIsEditorOpen(false);
  };

  const toggleLevel = (level: number) => {
    setExpandedLevels((previous) => ({ ...previous, [level]: !(previous[level] ?? false) }));
  };

  const handleAddTrick = (trick: TournamentTrick) => {
    setTrickNotice('');
    setTrickError('');
    if (selectedTricks.length >= KWC2026_POINT_MAX_TRICK_COUNT) {
      setTrickError(`最多只能选择 ${KWC2026_POINT_MAX_TRICK_COUNT} 个招式。`);
      return;
    }
    if (selectedTricks.includes(trick.id)) return;
    setSelectedTricks((previous) => [...previous, trick.id]);
  };

  const handleRemoveTrick = (index: number) => {
    setTrickNotice('');
    setTrickError('');
    setSelectedTricks((previous) => previous.filter((_, currentIndex) => currentIndex !== index));
  };

  const moveTrick = useCallback((fromIndex: number, toIndex: number) => {
    setSelectedTricks((previous) => {
      if (
        fromIndex < 0 ||
        fromIndex >= previous.length ||
        toIndex < 0 ||
        toIndex >= previous.length ||
        fromIndex === toIndex
      ) {
        return previous;
      }
      const next = [...previous];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  // 拖动排序：按下把手后直接接管指针，指针落到哪一行就排到哪一行。
  const handleDragStart = (event: React.PointerEvent<HTMLButtonElement>, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    dragStateRef.current = { pointerId: event.pointerId, fromIndex: index };
    setDraggingIndex(index);
    setDropIndex(index);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // 某些浏览器不支持指针捕获，退化成普通拖动即可
    }
  };

  const handleDragMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.preventDefault();

    const pointerY = event.clientY;
    let nextIndex = dropIndex ?? dragState.fromIndex;
    selectedRowRefs.current.forEach((row, index) => {
      if (!row) return;
      const rect = row.getBoundingClientRect();
      if (pointerY >= rect.top && pointerY <= rect.bottom) {
        nextIndex = index;
      }
    });
    if (nextIndex !== dropIndex) setDropIndex(nextIndex);

    // 拖到屏幕上下边缘时自动滚动，长列表也能拖到底
    if (pointerY < 96) window.scrollBy(0, -14);
    else if (pointerY > window.innerHeight - 96) window.scrollBy(0, 14);
  };

  const handleDragEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    dragStateRef.current = null;
    const targetIndex = dropIndex ?? dragState.fromIndex;
    setDraggingIndex(null);
    setDropIndex(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (targetIndex !== dragState.fromIndex) {
      moveTrick(dragState.fromIndex, targetIndex);
      setTrickNotice('');
      setTrickError('');
    }
  };

  const handleSaveTricks = async () => {
    if (isSavingTricks) return;
    setTrickNotice('');
    setTrickError('');
    if (selectedTricks.length === 0) {
      setTrickError('请先从 KWC2026 招式表里选择招式。');
      return;
    }
    setIsSavingTricks(true);
    try {
      const nextProfile = await saveKwc2026PointTricks(selectedTricks);
      setProfile(nextProfile);
      setSelectedTricks(nextProfile.selectedTricks);
      setTrickNotice(`招式已保存（${nextProfile.selectedTricks.length} 招）`);
    } catch (error) {
      setTrickError((error as Error)?.message || '招式保存失败');
    } finally {
      setIsSavingTricks(false);
    }
  };

  const handleVideoPick = async (file: File | null | undefined) => {
    if (!file || isUploading) return;
    setVideoError('');
    setSubmissionError('');
    setSubmissionNotice('');

    if (!isKwc2026PointVideoFile(file)) {
      setVideoError('请上传视频文件（mp4 / mov / webm 等）。');
      return;
    }
    if (file.size > KWC2026_POINT_MAX_VIDEO_SIZE) {
      setVideoError(`视频文件过大，请控制在 ${Math.round(KWC2026_POINT_MAX_VIDEO_SIZE / (1024 * 1024))}MB 以内。`);
      return;
    }

    try {
      const durationSeconds = await readKwc2026PointVideoDuration(file);
      const maxDuration = KWC2026_POINT_MAX_VIDEO_DURATION_SECONDS + KWC2026_POINT_MAX_VIDEO_DURATION_TOLERANCE_SECONDS;
      if (durationSeconds > maxDuration) {
        setVideoError(`视频时长 ${formatKwc2026PointDuration(durationSeconds)} 超过 3 分 20 秒，请重新上传。`);
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);
      const nextProfile = await uploadKwc2026PointVideo(file, Math.round(durationSeconds * 1000), (progress) => {
        setUploadProgress(progress);
      });
      setProfile(nextProfile);
      setSubmissionNotice('视频上传成功，可以点击投稿了。');
    } catch (error) {
      setVideoError((error as Error)?.message || '视频上传失败，请稍后重试。');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    setSubmissionError('');
    setSubmissionNotice('');
    if (!canSubmit) {
      setSubmissionError(
        selectedTricks.length === 0
          ? '请先选择并保存招式。'
          : !savedVideo
            ? '请先上传打招视频。'
            : hasUnsavedTricks
              ? '招式顺序有改动，请先保存招式再投稿。'
              : isVideoStale
                ? '招式在上传视频后有过改动，请重新上传视频后再投稿。'
                : '视频还没准备好，请等转码完成后再投稿。'
      );
      return;
    }
    setSubmissionPhase('submitting');
    try {
      const nextProfile = await submitKwc2026PointEntry();
      setProfile(nextProfile);
      setSelectedTricks(nextProfile.selectedTricks);
      setSubmissionPhase('idle');
      setSubmissionNotice('投稿成功，你已出现在排行榜中。');
      setIsTrickWindowOpen(false);
      setIsVideoWindowOpen(false);
      setIsEditorOpen(false);
      await loadLeaderboard();
      window.setTimeout(() => {
        leaderboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    } catch (error) {
      setSubmissionPhase('idle');
      setSubmissionError((error as Error)?.message || '投稿失败，请稍后重试。');
    }
  };

  const handleOpenDetail = async (entry: Kwc2026PointLeaderboardEntry) => {
    if (detailLoadingUserId) return;
    setDetailLoadingUserId(entry.userId);
    setDetailError('');
    setDetailEntry(null);
    try {
      const detail = await fetchKwc2026PointEntry(entry.userId);
      setDetailEntry(detail);
    } catch (error) {
      setDetailError((error as Error)?.message || '选手详情加载失败');
    } finally {
      setDetailLoadingUserId(null);
    }
  };

  const activeTrickGroups = useMemo(
    () => (activeLevel === 'ALL'
      ? kwc2026PointTrickGroups
      : kwc2026PointTrickGroups.filter((group) => group.level === activeLevel)),
    [activeLevel]
  );

  // 领奖台卡片：冠军（rank 1）放在中间且尺寸更大，二三名在两侧略小。
  const renderPodiumCard = (entry: Kwc2026PointLeaderboardEntry | undefined, rank: number) => {
    if (!entry) return null;
    const podium = PODIUM_STYLES[rank];
    const isChampion = rank === 1;
    const isSelf = entry.userId === user.uid;
    const isLoadingDetail = detailLoadingUserId === entry.userId;
    const columnClass = isChampion ? 'col-start-2' : rank === 2 ? 'col-start-1' : 'col-start-3';

    return (
      <button
        key={entry.userId}
        type="button"
        onClick={() => void handleOpenDetail(entry)}
        className={`${columnClass} row-start-1 flex min-w-0 flex-col items-center rounded-2xl border text-center transition-transform active:scale-[0.97] ${
          isChampion ? 'gap-2 px-2 py-4' : 'gap-1.5 px-1.5 py-3'
        } ${podium.card}`}
      >
        <span
          className={`flex shrink-0 items-center justify-center rounded-full border font-orbitron ${podium.badge} ${
            isChampion ? 'h-8 w-8 text-xs' : 'h-6 w-6 text-[11px]'
          }`}
        >
          {isChampion ? <Crown size={16} /> : rank}
        </span>
        <span
          className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5 font-orbitron text-white ${
            isChampion ? 'h-14 w-14 text-lg' : 'h-10 w-10 text-sm'
          }`}
        >
          {entry.avatarUrl
            ? <img src={entry.avatarUrl} alt="" className="h-full w-full object-cover" />
            : getAvatarInitial(entry.displayName)}
        </span>
        <span className={`line-clamp-2 w-full break-words font-semibold leading-tight text-white ${isChampion ? 'text-sm' : 'text-xs'}`}>
          {entry.displayName}
        </span>
        <span className={`font-orbitron leading-none ${podium.score} ${isChampion ? 'text-2xl' : 'text-lg'}`}>
          {isLoadingDetail ? <Loader2 size={isChampion ? 20 : 16} className="animate-spin" /> : entry.totalScore}
        </span>
        <span className={`uppercase tracking-[0.1em] text-zinc-500 ${isChampion ? 'text-[11px]' : 'text-[10px]'}`}>
          PTS · {entry.trickCount} 招
        </span>
        {(isSelf || isKwc2026PointLocalMockUserId(entry.userId)) && (
          <span
            className={`rounded-full border px-1.5 py-0.5 ${isChampion ? 'text-[10px]' : 'text-[9px]'} ${
              isSelf
                ? 'border-emerald-400/40 bg-emerald-500/15 text-emerald-100'
                : 'border-white/15 bg-white/5 text-zinc-400'
            }`}
          >
            {isSelf ? '我' : '测试'}
          </span>
        )}
      </button>
    );
  };

  const selectedTrickDetails = useMemo(
    () => selectedTricks.map((trickId) => ({
      id: trickId,
      level: kwc2026PointTrickList.find((trick) => trick.id === trickId)?.score ?? 0
    })),
    [selectedTricks]
  );

  const renderScoreBreakdown = (summary: ReturnType<typeof computeKwc2026PointScore>) => (
    <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
      <div className="min-w-0 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-1.5 py-2.5 text-center sm:px-3">
        <div className="truncate text-[9px] font-orbitron uppercase tracking-[0.1em] text-amber-200/80 sm:text-[10px] sm:tracking-[0.14em]">乘算分</div>
        <div className="mt-1 truncate font-orbitron text-base text-white sm:text-lg">{summary.squaredScore}</div>
      </div>
      <div className="min-w-0 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-1.5 py-2.5 text-center sm:px-3">
        <div className="truncate text-[9px] font-orbitron uppercase tracking-[0.1em] text-amber-200/80 sm:text-[10px] sm:tracking-[0.14em]">奖励分</div>
        <div className="mt-1 truncate font-orbitron text-base text-white sm:text-lg">{summary.bonusScore}</div>
      </div>
      <div className="min-w-0 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-1.5 py-2.5 text-center sm:px-3">
        <div className="truncate text-[9px] font-orbitron uppercase tracking-[0.1em] text-amber-200/80 sm:text-[10px] sm:tracking-[0.14em]">全成追加</div>
        <div className="mt-1 truncate font-orbitron text-base text-white sm:text-lg">{summary.allSuccessBonusScore}</div>
      </div>
    </div>
  );

  if (isProfileLoading) {
    return (
      <div data-i18n-skip="true" className="flex h-full w-full flex-col items-center justify-center gap-3 text-amber-100">
        <Loader2 size={26} className="animate-spin text-amber-300" />
        <div className="font-orbitron text-xs uppercase tracking-[0.2em] text-amber-200/80">KWC Simulated Finals</div>
        <div className="text-sm text-zinc-400">活动数据加载中...</div>
      </div>
    );
  }

  return (
    <div
      data-i18n-skip="true"
      className="flex h-full w-full flex-col overflow-hidden px-2 pt-2 md:px-5 md:py-4"
    >
      <div className="mx-auto flex h-full min-h-0 w-full max-w-6xl flex-col gap-3">
        {/* 顶部导航：主界面只保留返回 + 标题，其余内容都在悬浮按钮打开的投稿窗口里 */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-zinc-200 transition-colors hover:border-white/20 hover:bg-white/10"
          >
            <ArrowLeft size={16} />
            返回首页
          </button>
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2">
            <h1 className="min-w-0 truncate font-orbitron text-lg font-black tracking-[0.08em] text-white md:text-2xl">KWC模拟决赛</h1>
            <span className="shrink-0 rounded-full border border-amber-400/30 bg-amber-500/15 px-2.5 py-1 text-[10px] font-orbitron uppercase tracking-[0.16em] text-amber-100">
              试运行 BETA
            </span>
          </div>
        </div>

        {profileError && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            <span className="min-w-0 break-words">{profileError}</span>
            <button type="button" onClick={() => void loadProfile()} className="inline-flex shrink-0 items-center gap-1.5 text-xs text-red-100 underline">
              <RefreshCw size={12} />
              重试
            </button>
          </div>
        )}

        {/* 投稿界面窗口：挂到 body 上渲染。
            页面内容在 <main>（z-10）这个层叠上下文里，直接把 fixed 弹层放在里面会被外层
            顶栏（z-50）盖住关闭按钮，所以这里用 portal 跳出到 body。 */}
        {isEditorOpen && typeof document !== 'undefined' && createPortal(
        <div
          data-i18n-skip="true"
          className="fixed inset-0 z-[240] flex items-start justify-center overflow-y-auto overflow-x-hidden bg-black/75 px-3 py-5 backdrop-blur-sm md:px-6"
        >
          <div className="w-full min-w-0 max-w-5xl pb-28">
            <div className="mb-3 flex items-center justify-between gap-3 rounded-2xl border border-amber-400/25 bg-[#0b0b12]/95 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2 text-amber-200">
                <Send size={15} />
                <span className="shrink-0 font-orbitron text-sm uppercase tracking-[0.16em]">投稿界面</span>
                <span className="hidden truncate text-[11px] text-amber-100/70 sm:inline">选招 → 拖动排序 → 保存招式 → 上传视频 → 投稿</span>
              </div>
              <button
                type="button"
                onClick={closeEditor}
                className="shrink-0 rounded-lg border border-white/10 bg-white/5 p-1.5 text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10"
                aria-label="关闭投稿界面"
              >
                <X size={16} />
              </button>
            </div>

            {/* 规则说明 */}
            <div className="mb-3 w-full min-w-0 rounded-2xl border border-amber-400/20 bg-black/30 px-3 py-3 md:px-4">
              <div className="flex flex-wrap gap-2 text-[11px] text-amber-100/85">
                <span className="rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-1">① 从 KWC2026 表按顺序选招（可拖动排序）</span>
                <span className="rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-1">② 3 分钟内一次性打完并录像</span>
                <span className="rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-1">③ 视频 ≤ 3 分 20 秒</span>
                <span className="rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-1">④ 投稿后进入排行榜</span>
              </div>
              <p className="mt-2 break-words text-[11px] leading-relaxed text-amber-100/70">
                得分按积分赛 KWC2026 决赛规则：乘算分（等级²累加）+ 奖励分（13级+30、14级+100、15级+225）+ 全成追加（等级累加，整套全成时计入）。
              </p>
            </div>

            {/* 我的投稿状态 */}
            <section className={`${PANEL_CLASS} mb-3`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Trophy size={16} className="shrink-0 text-amber-300" />
                  <span className={SECTION_TITLE_CLASS}>我的投稿</span>
                </div>
                {submission ? (
                  <div className="max-w-full truncate rounded-full border border-emerald-400/30 bg-emerald-500/15 px-3 py-1 text-[10px] font-orbitron uppercase tracking-[0.14em] text-emerald-100">
                    已投稿 · {formatKwc2026PointTimestamp(submission.submittedAt)}
                  </div>
                ) : (
                  <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-orbitron uppercase tracking-[0.14em] text-zinc-400">
                    未投稿
                  </div>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="min-w-0 rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.14em] text-zinc-500">已保存招式</div>
                  <div className="mt-1 break-words text-sm text-white">
                    {savedTricks.length} 招
                    {hasUnsavedTricks ? <span className="ml-1 text-[11px] text-amber-300">（有未保存改动）</span> : null}
                  </div>
                </div>
                <div className="min-w-0 rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.14em] text-zinc-500">总分</div>
                  <div className="mt-1 truncate font-orbitron text-sm text-amber-200">
                    {(submission?.score.totalScore ?? computeKwc2026PointScore(savedTricks).totalScore)} PTS
                  </div>
                </div>
                <div className="min-w-0 rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.14em] text-zinc-500">视频</div>
                  <div className="mt-1 text-sm text-white">{videoStatusLabel}</div>
                </div>
                <div className="min-w-0 rounded-2xl border border-white/10 bg-black/25 px-3 py-2.5">
                  <div className="text-[10px] font-orbitron uppercase tracking-[0.14em] text-zinc-500">投稿状态</div>
                  <div className="mt-1 text-sm text-white">{submission ? '已投稿' : '未投稿'}</div>
                </div>
              </div>

              {submissionNotice && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                  <CheckCircle2 size={14} />
                  {submissionNotice}
                </div>
              )}
              {isVideoStale && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                  <AlertTriangle size={14} />
                  招式组在上传视频之后有过改动，当前视频与招式对不上，请重新上传视频后再投稿。
                </div>
              )}
              {submissionError && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-400/25 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                  <AlertTriangle size={14} />
                  {submissionError}
                </div>
              )}

              {submissionPhase === 'idle' ? (
                /* 投稿动作改成右下角悬浮按钮，这里只留说明 */
                <div className="mt-3 break-words text-[11px] leading-relaxed text-zinc-500">
                  {isVideoProcessing
                    ? '视频由独立转码机处理中，完成后右下角会变成可点的「投稿」。'
                    : '点右下角「投稿」按钮提交；投稿会以当前保存的招式组和视频为准，确认后出现在排行榜。'}
                </div>
              ) : (
                /* 确认步骤留在同一个窗口里，不再叠加第二层全屏遮罩 */
                <div className="mt-3 w-full min-w-0 rounded-2xl border border-amber-400/30 bg-amber-500/10 px-3 py-3">
                  <div className="flex items-center gap-2 text-amber-200">
                    <AlertTriangle size={16} className="shrink-0" />
                    <span className="font-orbitron text-xs uppercase tracking-[0.16em]">
                      {submissionPhase === 'submitting' ? '投稿中' : '确认投稿'}
                    </span>
                  </div>
                  <p className="mt-2 break-words text-xs leading-relaxed text-amber-100/85">
                    确定提交本次投稿？提交后你的昵称、总分与招式组会出现在排行榜，其他人点击即可查看视频。
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-amber-100/80">
                    <span>招式：{selectedTricks.length} 招</span>
                    <span>总分：<span className="font-orbitron text-amber-200">{scoreSummary.totalScore}</span> PTS</span>
                    <span>视频：{videoStatusLabel}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => void handleSubmit()}
                      disabled={submissionPhase === 'submitting'}
                      className={PRIMARY_BUTTON_CLASS}
                    >
                      {submissionPhase === 'submitting' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      确定投稿
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubmissionPhase('idle')}
                      disabled={submissionPhase === 'submitting'}
                      className={GHOST_BUTTON_CLASS}
                    >
                      取消
                    </button>
                  </div>
                </div>
              )}
        </section>
        </div>

        {/* 底部悬浮按钮组：选择招式 / 上传视频 / 投稿（子弹窗打开时整组隐藏，避免重叠） */}
        {submissionPhase === 'idle' && !isTrickWindowOpen && !isVideoWindowOpen && (
          <div className="fixed bottom-[calc(1.25rem_+_var(--safe-bottom))] right-4 z-[245] flex flex-col items-end gap-2 md:right-8">
            <button
              type="button"
              onClick={() => {
                setTrickWindowTab(selectedTricks.length > 0 ? 'order' : 'picker');
                setIsTrickWindowOpen(true);
              }}
              className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-black/75 px-4 py-2.5 font-orbitron text-xs font-bold uppercase tracking-[0.12em] text-amber-100 shadow-[0_10px_26px_rgba(0,0,0,0.5)] backdrop-blur transition-transform active:scale-95"
            >
              <ListOrdered size={14} />
              {savedTricks.length > 0 ? '修改招式组' : '选择招式'}
            </button>
            <button
              type="button"
              onClick={() => setIsVideoWindowOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-black/75 px-4 py-2.5 font-orbitron text-xs font-bold uppercase tracking-[0.12em] text-amber-100 shadow-[0_10px_26px_rgba(0,0,0,0.5)] backdrop-blur transition-transform active:scale-95"
            >
              <UploadCloud size={14} />
              {savedVideo ? '查看视频' : '上传视频'}
            </button>
            <button
              type="button"
              onClick={() => {
                setSubmissionError('');
                setSubmissionNotice('');
                if (!canSubmit) {
                  setSubmissionError(
                    selectedTricks.length === 0
                      ? '请先选择并保存招式。'
                      : !savedVideo
                        ? '请先上传打招视频。'
                        : isVideoProcessing
                          ? '视频还在转码中，请等转码完成后（进度 100%）再投稿。'
                          : didVideoFail
                            ? '视频转码失败，请重新上传后再投稿。'
                            : hasUnsavedTricks
                              ? '招式顺序有改动，请先保存招式再投稿。'
                              : isVideoStale
                                ? '招式在上传视频后有过改动，请重新上传视频后再投稿。'
                                : '视频还没准备好，请等转码完成后再投稿。'
                  );
                  return;
                }
                setSubmissionError('');
                setSubmissionPhase('confirm');
              }}
              aria-label={submission ? '重新投稿' : '投稿'}
              className="inline-flex items-center gap-2 rounded-full border border-amber-300/50 bg-[linear-gradient(135deg,#fbbf24,#d97706)] px-5 py-3 font-orbitron text-sm font-bold uppercase tracking-[0.12em] text-black shadow-[0_12px_32px_rgba(245,158,11,0.35)] transition-transform active:scale-95"
            >
              {isVideoProcessing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {isVideoProcessing
                ? `转码中${savedVideo?.processingProgress !== undefined ? ` ${savedVideo.processingProgress}%` : ''}`
                : submission ? '重新投稿' : '投稿'}
            </button>
          </div>
        )}

        </div>,
        document.body
        )}

        {/* 排行榜：去掉卡片边框，撑满剩余高度，列表内部滚动（全面屏下铺到屏幕底部） */}
        <section ref={leaderboardRef} className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 px-1">
            <div className="flex items-center gap-2">
              <Trophy size={16} className="text-amber-300" />
              <span className={SECTION_TITLE_CLASS}>排行榜</span>
            </div>
            <button
              type="button"
              onClick={() => void loadLeaderboard()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-[11px] text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10"
            >
              <RefreshCw size={12} className={isLeaderboardLoading ? 'animate-spin' : ''} />
              刷新
            </button>
          </div>

          {isLeaderboardLoading ? (
            <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-8 text-center text-sm text-zinc-500">
              排行榜加载中...
            </div>
          ) : leaderboardError ? (
            <div className="mt-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">{leaderboardError}</div>
          ) : leaderboard.length === 0 ? (
            <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-8 text-center text-sm text-zinc-500">
              还没有人投稿，完成 Step 1-3 就能成为第一位上榜选手。
            </div>
          ) : (
            <>
              {/* 领奖台：冠军居中最大，二三名在两侧（底对齐，形成台阶感） */}
              <div className="mt-2 grid grid-cols-3 items-end gap-2">
                {renderPodiumCard(leaderboard[1], 2)}
                {renderPodiumCard(leaderboard[0], 1)}
                {renderPodiumCard(leaderboard[2], 3)}
              </div>

              {/* 第 4 名及以后 */}
              <div className="mt-2 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain custom-scrollbar pb-28 pr-0.5">
              {leaderboard.slice(3).map((entry, index) => {
                const rank = index + 4;
                const isSelf = entry.userId === user.uid;
                const isLoadingDetail = detailLoadingUserId === entry.userId;
                return (
                  <button
                    key={entry.userId}
                    type="button"
                    onClick={() => void handleOpenDetail(entry)}
                    className={`flex items-center gap-3 rounded-2xl border px-3 py-2.5 text-left transition-colors ${
                      isSelf
                        ? 'border-emerald-400/35 bg-emerald-500/10 hover:bg-emerald-500/15'
                        : 'border-white/10 bg-black/25 hover:border-amber-400/30 hover:bg-amber-500/10'
                    }`}
                  >
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-orbitron text-xs ${RANK_STYLES[rank] || 'border-white/10 bg-white/5 text-zinc-300'}`}>
                      {rank <= 3 ? <Crown size={14} /> : rank}
                    </div>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5 text-sm font-orbitron text-white">
                      {entry.avatarUrl
                        ? <img src={entry.avatarUrl} alt="" className="h-full w-full object-cover" />
                        : getAvatarInitial(entry.displayName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-white">{entry.displayName}</span>
                        {isSelf && (
                          <span className="shrink-0 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-100">我</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-zinc-500">
                        <span>{entry.trickCount} 招 · {formatKwc2026PointTimestamp(entry.submittedAt)}</span>
                        {isKwc2026PointLocalMockUserId(entry.userId) && (
                          <span className="shrink-0 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] text-zinc-400">测试</span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-orbitron text-base text-amber-200">{entry.totalScore}</div>
                      <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500">PTS</div>
                    </div>
                    {isLoadingDetail
                      ? <Loader2 size={16} className="shrink-0 animate-spin text-amber-300" />
                      : <ChevronRight size={16} className="shrink-0 text-zinc-500" />}
                  </button>
                );
              })}
              </div>
            </>
          )}
        </section>
      </div>

      {/* 悬浮按钮：主界面只留排行榜，选招/上传/投稿/我的投稿都在这个按钮打开的窗口里 */}
      <button
        type="button"
        onClick={openEditor}
        aria-label={submission ? '我的投稿' : '投稿'}
        className="fixed bottom-[calc(1.25rem_+_var(--safe-bottom))] right-4 z-[230] inline-flex items-center gap-2 rounded-full border border-amber-300/50 bg-[linear-gradient(135deg,#fbbf24,#d97706)] px-5 py-3 font-orbitron text-sm font-bold uppercase tracking-[0.12em] text-black shadow-[0_12px_32px_rgba(245,158,11,0.35)] transition-transform active:scale-95 md:right-8"
      >
        <Send size={16} />
        {submission ? '我的投稿' : '投稿'}
      </button>

      {/* 招式选择窗口：顶部一行分数 + 选招/顺序切换 + 悬浮保存 */}
      {isTrickWindowOpen && typeof document !== 'undefined' && createPortal(
        <div
          data-i18n-skip="true"
          className="fixed inset-0 z-[270] flex flex-col overflow-hidden bg-black/80 px-3 py-4 backdrop-blur-sm md:px-6"
        >
          <div className="mx-auto flex h-full min-h-0 w-full max-w-5xl flex-col pb-24">
            <div className="mb-3 flex items-center justify-between gap-3 rounded-2xl border border-amber-400/25 bg-[#0b0b12]/95 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2 text-amber-200">
                <ListOrdered size={15} className="shrink-0" />
                <span className="shrink-0 font-orbitron text-sm uppercase tracking-[0.16em]">选择招式</span>
                <span className="hidden truncate text-[11px] text-amber-100/70 sm:inline">选招 → 拖动排序 → 保存招式</span>
              </div>
              <button
                type="button"
                onClick={() => setIsTrickWindowOpen(false)}
                className="shrink-0 rounded-lg border border-white/10 bg-white/5 p-1.5 text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10"
                aria-label="关闭招式窗口"
              >
                <X size={16} />
              </button>
            </div>

            {/* 分数预览：挪到顶部并压成一行 */}
            <div className="mb-3 flex w-full min-w-0 items-center gap-x-2.5 overflow-x-auto rounded-2xl border border-amber-400/25 bg-black/40 px-3 py-2.5 whitespace-nowrap md:gap-x-4 md:px-4">
              <span className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-orbitron uppercase tracking-[0.14em] text-amber-200/80">总分</span>
                <span className="font-orbitron text-lg leading-none text-amber-100">{scoreSummary.totalScore}</span>
                <span className="text-[10px] text-amber-100/60">PTS</span>
              </span>
              <span aria-hidden="true" className="h-4 w-px shrink-0 bg-white/10" />
              <span className="shrink-0 text-[11px] text-zinc-300">乘算分 <span className="font-orbitron text-white">{scoreSummary.squaredScore}</span></span>
              <span className="shrink-0 text-[11px] text-zinc-300">奖励分 <span className="font-orbitron text-white">{scoreSummary.bonusScore}</span></span>
              <span className="shrink-0 text-[11px] text-zinc-300">全成追加 <span className="font-orbitron text-white">{scoreSummary.allSuccessBonusScore}</span></span>
              {scoreSummary.breakdown.length > 0 && (
                <span className="hidden min-w-0 flex-1 items-center justify-end gap-1.5 overflow-hidden lg:flex">
                  {scoreSummary.breakdown.map((entry) => (
                    <span key={entry.score} className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-zinc-400">
                      {entry.score}分 ×{entry.count}
                    </span>
                  ))}
                </span>
              )}
            </div>

            {/* 选招 / 顺序：同一区域点击切换 */}
            <div className="mb-3 flex rounded-xl border border-white/10 bg-black/40 p-1">
              <button
                type="button"
                onClick={() => setTrickWindowTab('picker')}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-orbitron uppercase tracking-[0.12em] transition-colors ${
                  trickWindowTab === 'picker' ? 'bg-amber-500/25 text-amber-100' : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
               选择招式
              </button>
              <button
                type="button"
                onClick={() => setTrickWindowTab('order')}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-orbitron uppercase tracking-[0.12em] transition-colors ${
                  trickWindowTab === 'order' ? 'bg-amber-500/25 text-amber-100' : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
               招式顺序（{selectedTricks.length}）
              </button>
            </div>

            <div className={trickWindowTab === 'picker' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>

          {/* 招式选择 + 顺序调整 */}
          <section className={`${PANEL_CLASS} flex min-h-0 flex-1 flex-col`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ListOrdered size={16} className="text-amber-300" />
                <span className={SECTION_TITLE_CLASS}>Step 1 · 选择招式</span>
              </div>
              <div className="text-[11px] text-amber-100/70">
                已选 <span className="font-orbitron text-amber-200">{selectedTricks.length}</span> / {KWC2026_POINT_MAX_TRICK_COUNT}
              </div>
            </div>

            {/* 等级筛选 */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setActiveLevel('ALL')}
                className={`rounded-lg border px-2.5 py-1 text-[11px] font-orbitron uppercase tracking-[0.12em] transition-colors ${
                  activeLevel === 'ALL'
                    ? 'border-amber-400/50 bg-amber-500/20 text-amber-100'
                    : 'border-white/10 bg-white/5 text-zinc-400 hover:text-zinc-200'
                }`}
              >
                All
              </button>
              {kwc2026PointTrickGroups.map((group) => (
                <button
                  key={group.level}
                  type="button"
                  onClick={() => setActiveLevel(group.level)}
                  className={`rounded-lg border px-2.5 py-1 text-[11px] font-orbitron transition-colors ${
                    activeLevel === group.level
                      ? 'border-amber-400/50 bg-amber-500/20 text-amber-100'
                      : 'border-white/10 bg-white/5 text-zinc-400 hover:text-zinc-200'
                  }`}
                >
                  Lv{group.level}
                </button>
              ))}
            </div>

            {/* 招式总表 */}
            <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto custom-scrollbar pr-1">
              {activeTrickGroups.map((group) => {
                const isExpanded = activeLevel !== 'ALL' || (expandedLevels[group.level] ?? false);
                const selectedCountInGroup = group.tricks.filter((trick) => selectedTricks.includes(trick.id)).length;
                return (
                  <div key={group.level} className="overflow-hidden rounded-2xl border border-white/8 bg-black/25">
                    <button
                      type="button"
                      onClick={() => {
                        if (activeLevel !== 'ALL') return;
                        toggleLevel(group.level);
                      }}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/[0.03]"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="rounded-md bg-amber-600/80 px-2 py-0.5 font-orbitron text-[10px] font-bold text-black">
                          {group.level} 级
                        </span>
                        <span className="font-rajdhani text-xs text-zinc-400">
                          {group.tricks.length} 招{selectedCountInGroup > 0 ? ` · 已选 ${selectedCountInGroup}` : ''}
                        </span>
                      </div>
                      {activeLevel === 'ALL' && (
                        isExpanded
                          ? <ChevronDown size={16} className="shrink-0 text-amber-300" />
                          : <ChevronRight size={16} className="shrink-0 text-amber-300" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="grid grid-cols-1 gap-2 border-t border-white/8 p-2.5 md:grid-cols-2">
                        {group.tricks.map((trick) => {
                          const isSelected = selectedTricks.includes(trick.id);
                          const { title, chinese } = splitKwcPointTrickName(trick.id);
                          return (
                            <button
                              key={trick.id}
                              type="button"
                              onClick={() => handleAddTrick(trick)}
                              disabled={isSelected}
                              className={`flex items-start justify-between gap-2 rounded-xl border px-3 py-2 text-left transition-colors ${
                                isSelected
                                  ? 'cursor-default border-amber-400/40 bg-amber-500/15'
                                  : 'border-white/10 bg-black/30 hover:border-amber-400/30 hover:bg-amber-500/10'
                              }`}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="line-clamp-2 break-words font-rajdhani text-sm text-white">{title}</div>
                                <div className="line-clamp-2 break-words text-[11px] text-amber-100/70">{chinese}</div>
                              </div>
                              <span className="shrink-0 font-orbitron text-[10px] uppercase tracking-[0.12em] text-amber-200/80">
                                {isSelected ? '已选' : `${trick.score}分`}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
            </div>

            <div className={trickWindowTab === 'order' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
          <section className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
            <div className={`${PANEL_CLASS} flex min-h-0 flex-1 flex-col`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <GripVertical size={16} className="text-amber-300" />
                  <span className={SECTION_TITLE_CLASS}>招式顺序</span>
                </div>
                <div className="flex items-center gap-2">
                  {selectedTricks.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTricks([]);
                        setTrickNotice('');
                        setTrickError('');
                      }}
                      className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-zinc-400 transition-colors hover:text-zinc-200"
                    >
                      清空
                    </button>
                  )}
                </div>
              </div>

              <div className="mt-2 text-[11px] text-zinc-500">按住左侧把手上下拖动即可调整出场顺序，顺序会随投稿一起保存。</div>

              {trickNotice && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                  <CheckCircle2 size={14} />
                  {trickNotice}
                </div>
              )}
              {trickError && (
                <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-400/25 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                  <AlertTriangle size={14} />
                  {trickError}
                </div>
              )}

              <div className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto custom-scrollbar pr-1">
                {selectedTrickDetails.length === 0 ? (
                  <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-6 text-center text-sm text-zinc-500">
                    还没有选择招式，先从左侧招式表里挑几个吧。
                  </div>
                ) : (
                  selectedTrickDetails.map((item, index) => {
                    const { title, chinese } = splitKwcPointTrickName(item.id);
                    const isDragging = draggingIndex === index;
                    const isDropTarget = draggingIndex !== null && dropIndex === index && draggingIndex !== index;
                    return (
                      <div
                        key={`${item.id}-${index}`}
                        ref={(element) => { selectedRowRefs.current[index] = element; }}
                        className={`flex items-center gap-2 rounded-2xl border px-2.5 py-2 transition-colors ${
                          isDragging
                            ? 'border-amber-400/60 bg-amber-500/20'
                            : isDropTarget
                              ? 'border-amber-400/50 bg-amber-500/10'
                              : 'border-white/10 bg-black/25'
                        }`}
                      >
                        <button
                          type="button"
                          onPointerDown={(event) => handleDragStart(event, index)}
                          onPointerMove={handleDragMove}
                          onPointerUp={handleDragEnd}
                          onPointerCancel={handleDragEnd}
                          className="shrink-0 touch-none rounded-lg border border-white/10 bg-white/5 p-1.5 text-zinc-400 transition-colors hover:text-amber-200"
                          aria-label={`拖动调整第 ${index + 1} 招`}
                        >
                          <GripVertical size={14} />
                        </button>
                        <div className="w-6 shrink-0 text-center font-orbitron text-[11px] text-amber-200/80">{index + 1}</div>
                        <div className="min-w-0 flex-1">
                          <div className="line-clamp-2 break-words font-rajdhani text-sm text-white">{title}</div>
                          <div className="line-clamp-2 break-words text-[11px] text-amber-100/70">{chinese}</div>
                        </div>
                        <div className="shrink-0 rounded-md bg-amber-600/80 px-1.5 py-0.5 font-orbitron text-[10px] font-bold text-black">
                          {item.level}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveTrick(index)}
                          className="shrink-0 rounded-lg border border-white/10 bg-white/5 p-1.5 text-zinc-400 transition-colors hover:border-red-400/40 hover:text-red-200"
                          aria-label={`移除第 ${index + 1} 招`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>
            </div>
          </div>

          {/* 保存招式：悬浮按钮 */}
          <button
            type="button"
            onClick={() => void handleSaveTricks()}
            disabled={isSavingTricks || selectedTricks.length === 0}
            className="fixed bottom-[calc(1.25rem_+_var(--safe-bottom))] right-4 z-[280] inline-flex items-center gap-2 rounded-full border border-amber-300/50 bg-[linear-gradient(135deg,#fbbf24,#d97706)] px-5 py-3 font-orbitron text-sm font-bold uppercase tracking-[0.12em] text-black shadow-[0_12px_32px_rgba(245,158,11,0.35)] transition-transform active:scale-95 disabled:opacity-40 md:right-8"
          >
            {isSavingTricks ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            保存招式
          </button>
        </div>,
        document.body
      )}

      {/* 视频上传窗口：上传 / 转码状态 / 预览，独立弹窗 */}
      {isVideoWindowOpen && typeof document !== 'undefined' && createPortal(
        <div
          data-i18n-skip="true"
          className="fixed inset-0 z-[275] flex items-center justify-center bg-black/50 px-3 py-4 backdrop-blur-sm md:px-6"
        >
          <div className="flex max-h-[calc(100dvh-6rem)] w-full min-w-0 max-w-md flex-col overflow-y-auto overflow-x-hidden rounded-[26px] border border-amber-400/30 bg-[#0b0b12]/95 p-4 shadow-[0_24px_60px_rgba(0,0,0,0.6)] md:p-5">
            <div className="mb-3 flex items-center justify-between gap-3 border-b border-white/10 pb-3">
              <div className="flex min-w-0 items-center gap-2 text-amber-200">
                <Film size={15} className="shrink-0" />
                <span className="shrink-0 font-orbitron text-sm uppercase tracking-[0.16em]">上传打招视频</span>
              </div>
              <button
                type="button"
                onClick={() => setIsVideoWindowOpen(false)}
                className="shrink-0 rounded-lg border border-white/10 bg-white/5 p-1.5 text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10"
                aria-label="关闭视频窗口"
              >
                <X size={16} />
              </button>
            </div>
        {/* Step 2 · 视频 */}
        <section className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Film size={16} className="text-amber-300" />
              <span className={SECTION_TITLE_CLASS}>Step 2 · 上传打招视频</span>
            </div>
            <div className="text-[11px] text-amber-100/70">限 3 分 20 秒以内（3 分钟打完 + 入场余量）</div>
          </div>

          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(event) => void handleVideoPick(event.target.files?.[0])}
          />

          {videoError && (
            <div className="mt-3 flex items-center gap-2 rounded-xl border border-red-400/25 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              <AlertTriangle size={14} />
              {videoError}
            </div>
          )}

          {isUploading ? (
            <div className="mt-3 rounded-2xl border border-amber-400/25 bg-amber-500/10 px-4 py-4">
              <div className="flex items-center gap-2 text-sm text-amber-100">
                <Loader2 size={16} className="animate-spin" />
                视频上传中{uploadProgress !== null ? ` ${uploadProgress}%` : ''}
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-black/40">
                <div
                  className="h-full rounded-full bg-amber-400 transition-all duration-300"
                  style={{ width: `${uploadProgress ?? 0}%` }}
                />
              </div>
              <div className="mt-2 text-[11px] text-amber-100/70">大文件请保持页面在前台，上传完成前不要关闭。</div>
            </div>
          ) : savedVideo ? (
            <div className="mt-3 space-y-3">
              {isVideoProcessing ? (
                <div className="w-full min-w-0 rounded-2xl border border-sky-400/25 bg-sky-500/10 px-3 py-3">
                  <div className="flex items-center gap-2 text-sm text-sky-100">
                    <Loader2 size={16} className="shrink-0 animate-spin" />
                    转码中{savedVideo.processingProgress !== undefined ? ` ${savedVideo.processingProgress}%` : ''}
                  </div>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-black/40">
                    <div
                      className="h-full rounded-full bg-sky-400 transition-all duration-500"
                      style={{ width: `${Math.max(4, savedVideo.processingProgress ?? 4)}%` }}
                    />
                  </div>
                  <div className="mt-2 break-words text-[11px] leading-relaxed text-sky-100/70">
                    视频已上传，正在由独立转码机处理成可播放格式（与 CKO2026 线上赛同一套流程），通常 1-3 分钟。完成后这里会自动切换成可播放，届时才能投稿。
                  </div>
                </div>
              ) : didVideoFail ? (
                <div className="w-full min-w-0 rounded-2xl border border-red-400/25 bg-red-500/10 px-3 py-2.5 text-xs text-red-200">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={14} className="shrink-0" />
                    转码失败
                  </div>
                  <div className="mt-1 break-words text-red-100/80">
                    {savedVideo.processingMessage || '视频处理失败，请重新上传或改用 MP4 文件。'}
                  </div>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
                  <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
                    <video src={savedVideo.url} controls playsInline preload="metadata" className="h-full w-full object-contain" />
                  </div>
                  <div className="flex min-w-0 flex-col justify-between gap-3">
                    <div className="space-y-1 break-words text-[11px] text-zinc-400">
                      <div>上传时间：{formatKwc2026PointTimestamp(savedVideo.uploadedAt)}</div>
                      {!!savedVideo.fileName && <div className="break-all">文件名：{savedVideo.fileName}</div>}
                      <div className="flex flex-wrap gap-2">
                        {!!savedVideo.durationMs && <span>时长：{formatKwc2026PointDuration(savedVideo.durationMs / 1000)}</span>}
                        {!!savedVideo.size && <span>大小：{formatVideoSize(savedVideo.size)}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-3 flex flex-col items-center gap-2 rounded-2xl border border-dashed border-amber-400/30 bg-black/25 px-4 py-8 text-center">
              <UploadCloud size={26} className="text-amber-300" />
              <div className="text-sm text-amber-100/80">上传一段 3 分 20 秒以内的视频，内容为 3 分钟内一次打完所选招式组。</div>
              <div className="text-[11px] text-amber-100/60">点右下角悬浮按钮选择视频文件。</div>
            </div>
          )}
        </section>
          </div>

          {/* 上传 / 重新上传：固定悬浮按钮 */}
          <button
            type="button"
            onClick={() => videoInputRef.current?.click()}
            disabled={isUploading}
            className="fixed bottom-[calc(1.25rem_+_var(--safe-bottom))] right-4 z-[280] inline-flex items-center gap-2 rounded-full border border-amber-300/50 bg-[linear-gradient(135deg,#fbbf24,#d97706)] px-5 py-3 font-orbitron text-sm font-bold uppercase tracking-[0.12em] text-black shadow-[0_12px_32px_rgba(245,158,11,0.35)] transition-transform active:scale-95 disabled:opacity-40 md:right-8"
          >
            {isUploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
            {savedVideo ? '重新上传' : '上传视频'}
          </button>

        </div>,
        document.body
      )}

      {/* 选手详情 */}
      {(detailEntry || detailLoadingUserId || detailError) && typeof document !== 'undefined' && createPortal(
        <div
          data-i18n-skip="true"
          className="fixed inset-0 z-[250] flex items-start justify-center overflow-y-auto overflow-x-hidden bg-black/75 px-3 py-6 backdrop-blur-sm md:px-6"
        >
          <div className="w-full max-w-3xl rounded-[24px] border border-amber-400/25 bg-[#0b0b12]/96 p-4 shadow-[0_0_40px_rgba(245,158,11,0.14)] md:p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-amber-200">
                <Trophy size={16} />
                <span className="font-orbitron text-sm uppercase tracking-[0.16em]">选手详情</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDetailEntry(null);
                  setDetailError('');
                  setDetailLoadingUserId(null);
                }}
                className="rounded-lg border border-white/10 bg-white/5 p-1.5 text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10"
                aria-label="关闭"
              >
                <X size={16} />
              </button>
            </div>

            {detailLoadingUserId && !detailEntry ? (
              <div className="mt-4 flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-6 text-sm text-zinc-400">
                <Loader2 size={16} className="animate-spin text-amber-300" />
                选手详情加载中...
              </div>
            ) : detailError && !detailEntry ? (
              <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">{detailError}</div>
            ) : detailEntry ? (
              <div className="mt-4 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/5 text-base font-orbitron text-white">
                    {detailEntry.avatarUrl
                      ? <img src={detailEntry.avatarUrl} alt="" className="h-full w-full object-cover" />
                      : getAvatarInitial(detailEntry.displayName)}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate font-orbitron text-lg text-white">{detailEntry.displayName}</div>
                    <div className="text-[11px] text-zinc-500">
                      {detailEntry.trickCount} 招 · 投稿于 {formatKwc2026PointTimestamp(detailEntry.submittedAt)}
                    </div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="font-orbitron text-2xl text-amber-200">{detailEntry.score.totalScore}</div>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500">Total PTS</div>
                  </div>
                </div>

                {detailEntry.video ? (
                  <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
                    <video
                      src={detailEntry.video.url}
                      controls
                      playsInline
                      preload="metadata"
                      className="max-h-[46vh] w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-6 text-center text-sm text-zinc-500">
                    该选手没有可播放的投稿视频。
                  </div>
                )}

                {renderScoreBreakdown(detailEntry.score)}

                <div className="flex flex-wrap gap-1.5">
                  {detailEntry.score.breakdown.map((entry) => (
                    <span key={entry.score} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] text-zinc-300">
                      {entry.score}分 ×{entry.count} = 乘算 {entry.squaredSubtotal}
                      {entry.bonusSubtotal > 0 ? ` / 奖励 +${entry.bonusSubtotal}` : ''}
                    </span>
                  ))}
                </div>

                <div>
                  <div className="flex items-center gap-2">
                    <ListOrdered size={14} className="text-amber-300" />
                    <span className="font-orbitron text-xs uppercase tracking-[0.16em] text-amber-200">招式组（按出场顺序）</span>
                  </div>
                  <div className="mt-2 space-y-2">
                    {detailEntry.tricks.map((trick, index) => {
                      const { title, chinese } = splitKwcPointTrickName(trick.id);
                      return (
                        <div key={`${trick.id}-${index}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/25 px-3 py-2">
                          <div className="w-6 shrink-0 text-center font-orbitron text-[11px] text-amber-200/80">{index + 1}</div>
                          <div className="min-w-0 flex-1">
                            <div className="line-clamp-2 break-words font-rajdhani text-sm text-white">{title}</div>
                            <div className="line-clamp-2 break-words text-[11px] text-amber-100/70">{chinese}</div>
                          </div>
                          <div className="shrink-0 rounded-md bg-amber-600/80 px-1.5 py-0.5 font-orbitron text-[10px] font-bold text-black">
                            {trick.level} 级
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default KWC2026PointMode;
